// src/Studio/Avatar/faceLandmarker.ts
// RESPONSIBILITY: MediaPipe Face Landmarker singleton wrapper
// Provides face detection, landmarks, and blendshapes for avatar expressions

import {
  FaceLandmarker,
  FilesetResolver,
  type FaceLandmarkerResult,
  type Classifications,
  type NormalizedLandmark,
} from '@mediapipe/tasks-vision';

// CDN paths for MediaPipe WASM files
const MEDIAPIPE_CDN = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm';
const MODEL_URL = 'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task';

// Singleton instance
let faceLandmarkerInstance: FaceLandmarker | null = null;
let isInitializing = false;
let initPromise: Promise<FaceLandmarker> | null = null;

/**
 * Initialize the MediaPipe Face Landmarker singleton.
 * Safe to call multiple times - returns existing instance if already initialized.
 */
export async function initFaceLandmarker(): Promise<FaceLandmarker> {
  // Return existing instance
  if (faceLandmarkerInstance) {
    return faceLandmarkerInstance;
  }

  // Return existing initialization promise
  if (initPromise) {
    return initPromise;
  }

  isInitializing = true;

  initPromise = (async () => {
    try {
      // Load WASM files
      const filesetResolver = await FilesetResolver.forVisionTasks(MEDIAPIPE_CDN);

      // Create Face Landmarker with VIDEO mode for real-time detection
      const landmarker = await FaceLandmarker.createFromOptions(filesetResolver, {
        baseOptions: {
          modelAssetPath: MODEL_URL,
          delegate: 'GPU', // Use GPU acceleration when available
        },
        runningMode: 'VIDEO',
        numFaces: 10, // Support multiple faces
        outputFaceBlendshapes: true, // Enable expression blendshapes
        outputFacialTransformationMatrixes: true, // Enable face transform matrices
      });

      faceLandmarkerInstance = landmarker;
      console.log('[FaceLandmarker] MediaPipe Face Landmarker initialized');
      return landmarker;
    } catch (error) {
      console.error('[FaceLandmarker] Failed to initialize:', error);
      initPromise = null;
      throw error;
    } finally {
      isInitializing = false;
    }
  })();

  return initPromise;
}

/**
 * Detect faces in a video frame.
 * @param video - Video element to detect faces in
 * @param timestampMs - Current video timestamp in milliseconds
 * @returns Face landmarker result with landmarks, blendshapes, and transforms
 */
export async function detectForVideo(
  video: HTMLVideoElement,
  timestampMs: number
): Promise<FaceLandmarkerResult> {
  const landmarker = await initFaceLandmarker();
  return landmarker.detectForVideo(video, timestampMs);
}

/**
 * Detect faces in an image (single frame detection).
 * @param image - Image element or canvas to detect faces in
 * @returns Face landmarker result with landmarks, blendshapes, and transforms
 */
export async function detectInImage(
  image: HTMLImageElement | HTMLCanvasElement
): Promise<FaceLandmarkerResult> {
  const landmarker = await initFaceLandmarker();

  // For image detection, we need to switch to IMAGE mode temporarily
  // or use a workaround by treating it as a video frame
  // MediaPipe supports this via detect() method
  return landmarker.detect(image);
}

/**
 * Check if Face Landmarker is initialized.
 */
export function isInitialized(): boolean {
  return faceLandmarkerInstance !== null;
}

/**
 * Check if Face Landmarker is currently initializing.
 */
export function isLoading(): boolean {
  return isInitializing;
}

/**
 * Dispose the Face Landmarker instance and free resources.
 */
export function disposeFaceLandmarker(): void {
  if (faceLandmarkerInstance) {
    faceLandmarkerInstance.close();
    faceLandmarkerInstance = null;
    initPromise = null;
    console.log('[FaceLandmarker] Disposed');
  }
}

// ============================================================================
// BLENDSHAPE HELPERS
// ============================================================================

/**
 * Standard MediaPipe face blendshape categories.
 * These map to ARKit/MetaHuman compatible blendshape names.
 */
export const BLENDSHAPE_NAMES = [
  '_neutral',
  'browDownLeft',
  'browDownRight',
  'browInnerUp',
  'browOuterUpLeft',
  'browOuterUpRight',
  'cheekPuff',
  'cheekSquintLeft',
  'cheekSquintRight',
  'eyeBlinkLeft',
  'eyeBlinkRight',
  'eyeLookDownLeft',
  'eyeLookDownRight',
  'eyeLookInLeft',
  'eyeLookInRight',
  'eyeLookOutLeft',
  'eyeLookOutRight',
  'eyeLookUpLeft',
  'eyeLookUpRight',
  'eyeSquintLeft',
  'eyeSquintRight',
  'eyeWideLeft',
  'eyeWideRight',
  'jawForward',
  'jawLeft',
  'jawOpen',
  'jawRight',
  'mouthClose',
  'mouthDimpleLeft',
  'mouthDimpleRight',
  'mouthFrownLeft',
  'mouthFrownRight',
  'mouthFunnel',
  'mouthLeft',
  'mouthLowerDownLeft',
  'mouthLowerDownRight',
  'mouthPressLeft',
  'mouthPressRight',
  'mouthPucker',
  'mouthRight',
  'mouthRollLower',
  'mouthRollUpper',
  'mouthShrugLower',
  'mouthShrugUpper',
  'mouthSmileLeft',
  'mouthSmileRight',
  'mouthStretchLeft',
  'mouthStretchRight',
  'mouthUpperUpLeft',
  'mouthUpperUpRight',
  'noseSneerLeft',
  'noseSneerRight',
] as const;

export type BlendshapeName = typeof BLENDSHAPE_NAMES[number];

/**
 * Extract blendshape values as a simple key-value object.
 * @param blendshapes - Classifications array from FaceLandmarkerResult
 * @param faceIndex - Index of the face to extract blendshapes for
 * @returns Object mapping blendshape names to their scores (0-1)
 */
export function extractBlendshapes(
  blendshapes: Classifications[] | undefined,
  faceIndex: number = 0
): Record<string, number> {
  if (!blendshapes || !blendshapes[faceIndex]) {
    return {};
  }

  const result: Record<string, number> = {};
  const faceBlendshapes = blendshapes[faceIndex];

  for (const category of faceBlendshapes.categories) {
    result[category.categoryName] = category.score;
  }

  return result;
}

/**
 * Extract simplified expression states from blendshapes.
 * Useful for quick expression detection without full blendshape data.
 */
export function extractSimpleExpressions(
  blendshapes: Classifications[] | undefined,
  faceIndex: number = 0
): {
  isSmiling: boolean;
  isFrowning: boolean;
  isEyesClosed: boolean;
  isMouthOpen: boolean;
  isEyebrowsRaised: boolean;
} {
  const bs = extractBlendshapes(blendshapes, faceIndex);

  const leftSmile = bs['mouthSmileLeft'] ?? 0;
  const rightSmile = bs['mouthSmileRight'] ?? 0;
  const leftFrown = bs['mouthFrownLeft'] ?? 0;
  const rightFrown = bs['mouthFrownRight'] ?? 0;
  const leftBlink = bs['eyeBlinkLeft'] ?? 0;
  const rightBlink = bs['eyeBlinkRight'] ?? 0;
  const jawOpen = bs['jawOpen'] ?? 0;
  const browUp = bs['browInnerUp'] ?? 0;

  return {
    isSmiling: (leftSmile + rightSmile) / 2 > 0.3,
    isFrowning: (leftFrown + rightFrown) / 2 > 0.3,
    isEyesClosed: (leftBlink + rightBlink) / 2 > 0.5,
    isMouthOpen: jawOpen > 0.3,
    isEyebrowsRaised: browUp > 0.3,
  };
}

// ============================================================================
// LANDMARK HELPERS
// ============================================================================

/**
 * Key landmark indices for common facial features.
 * Based on MediaPipe Face Mesh 478 landmarks.
 */
export const LANDMARK_INDICES = {
  // Eyes
  leftEyeInner: 133,
  leftEyeOuter: 33,
  leftEyeTop: 159,
  leftEyeBottom: 145,
  rightEyeInner: 362,
  rightEyeOuter: 263,
  rightEyeTop: 386,
  rightEyeBottom: 374,

  // Eyebrows
  leftEyebrowInner: 107,
  leftEyebrowOuter: 70,
  rightEyebrowInner: 336,
  rightEyebrowOuter: 300,

  // Nose
  noseTip: 1,
  noseBottom: 2,
  noseLeft: 98,
  noseRight: 327,

  // Mouth
  mouthLeft: 61,
  mouthRight: 291,
  mouthTop: 13,
  mouthBottom: 14,

  // Face contour
  chin: 152,
  leftCheek: 234,
  rightCheek: 454,
  foreheadCenter: 10,
} as const;

/**
 * Get the bounding box of a face from its landmarks.
 * @param landmarks - Face landmarks array
 * @param imageWidth - Width of the source image/video
 * @param imageHeight - Height of the source image/video
 * @returns Bounding box with x, y, width, height in pixels
 */
export function getLandmarksBoundingBox(
  landmarks: NormalizedLandmark[],
  imageWidth: number,
  imageHeight: number
): { x: number; y: number; width: number; height: number } {
  if (!landmarks || landmarks.length === 0) {
    return { x: 0, y: 0, width: 0, height: 0 };
  }

  let minX = 1, maxX = 0, minY = 1, maxY = 0;

  for (const landmark of landmarks) {
    minX = Math.min(minX, landmark.x);
    maxX = Math.max(maxX, landmark.x);
    minY = Math.min(minY, landmark.y);
    maxY = Math.max(maxY, landmark.y);
  }

  return {
    x: minX * imageWidth,
    y: minY * imageHeight,
    width: (maxX - minX) * imageWidth,
    height: (maxY - minY) * imageHeight,
  };
}

/**
 * Get specific landmark position in pixel coordinates.
 * @param landmarks - Face landmarks array
 * @param index - Landmark index
 * @param imageWidth - Width of the source image/video
 * @param imageHeight - Height of the source image/video
 */
export function getLandmarkPosition(
  landmarks: NormalizedLandmark[],
  index: number,
  imageWidth: number,
  imageHeight: number
): { x: number; y: number; z: number } | null {
  const landmark = landmarks[index];
  if (!landmark) return null;

  return {
    x: landmark.x * imageWidth,
    y: landmark.y * imageHeight,
    z: landmark.z * imageWidth, // z is relative to x scale
  };
}

// Re-export types for convenience
export type { FaceLandmarkerResult, Classifications, NormalizedLandmark };
